import React from 'react';
import { sum, sumBy, get, set, intersection } from 'lodash';
import memoize from 'fast-memoize';
import pluralize from 'pluralize';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import { ratio, safeDivide, simpleSum } from 'components/atoms/tables/smarttables/helpers/aggregations';
import ImageCellDiv from 'components/molecules/tables/components/ImageCellDiv';

exports.formatColumnsForExport = (columns) => {
    if (columns.some(x => x.Footer)) {
        return columns.map((x) => {
            if (!x.Footer) {
                x.Footer = (<CellDiv value={'N/A'} type={'NOT_APPLICABLE'} strong />);
            }
            return x;
        });
    }
    return columns;
};
const generateLabelCodeHeaderMap = (labelCode, defaultLabel) => {
    if (!labelCode || labelCode === '') {
        return defaultLabel;
    }

    // This is just moving tech debt from one place to another. We should extract all the needed terms at the container
    // level from the objectiveSettings helper and pass them along. Deferring for now cuz it would involve a ton
    // of changes to all containers and several tables and their children.
    const singularTerm = pluralize.singular(labelCode);
    const pluralTerm = pluralize.plural(labelCode);

    switch (defaultLabel) {
    case 'eCPA ACTIVITIES':
        return pluralTerm.toUpperCase();
    case 'CAMPAIGN ACTIVITIES':
        return `TOTAL ${pluralTerm.toUpperCase()}`;
    case 'TOTAL ACTIVITIES':
        return `CAMPAIGN ${pluralTerm.toUpperCase()}`;
    case 'eCPA':
        if (labelCode === 'Conversion') {
            return 'COST / CONV';
        }
        return `COST/${singularTerm.toUpperCase()}`;
    case 'eCPA RATE':
        return `COST/${singularTerm.toUpperCase()} RATE`;
    case 'eCPA BUDGET':
        return `${pluralTerm.toUpperCase()} BUDGET`;
    case 'CONVERSIONS':
        return `${pluralTerm.toUpperCase()}`;
    case 'CONVERSION RATE':
        return `${singularTerm.toUpperCase()} RATE`;
    case 'COST / CONV':
        return `COST / ${singularTerm.toUpperCase()}`;
    default:
        throw new Error(`No labelcode case for ${defaultLabel}`);
    }
};

function _traverseSubRows(row, key) {
    if (key in row) {
        return row[key];
    } else if ('_original' in row) {
        return get(row, ['_original', key]);
    } else if ('_subRows' in row) {
        return row._subRows.reduce((prev, r) => prev + _traverseSubRows(r, key), 0);
    }
    // the catch all - should not be hit
    return _nestedSumBy(row, key); // eslint-disable-line no-use-before-define
}

function _nestedSumBy(row, key) {
    if ('_original' in row) {
        return get(row, ['_original', key]);
    } else if ('subRows' in row) {
        return sumBy(row.subRows, r => _traverseSubRows(r, key));
    }
    // This should not be hit
    return 0;
}

exports.nestedSumBy = memoize(_nestedSumBy);

const hasCalcMetric = (row, numerator) => !!get(row, ['row', 'original', numerator]); // check if row has value needed for calculation

const createCellDiv = (value, type) => (<CellDiv value={value} type={type} />);

const replaceNotApplicableVals = (row, type) => (row.value === null ? createCellDiv('--', 'NOT_APPLICABLE') : createCellDiv(row.value, type));

const columnsTemplateLookup = (col, { labelCode, metricPrefix } = {}) => {
    switch (col) {
    case 'has_delivered':
        return {
            Header: 'HAS DELIVERED',
            accessor: 'has_delivered',
            type: 'boolean',
            aggregate: () => 'N/A',
            filter: 'booleanFilter',
            Cell: row => <CellDiv value={row.value ? 'True' : 'False'} type={'boolean'} />,
            dataToggle: {
                filter: 'is',
                value: true
            },
            nowrap: true
        };
    case 'impressions':
        return {
            Header: 'IMPRESSIONS',
            accessor: 'impressions',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'impressions')
        };
    case 'eCPAActivities':
    case 'ecpaActivities':
    case 'ecpa_activities':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'eCPA ACTIVITIES'),
            accessor: 'ecpa_activities',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'ecpa_activities')
        };
    case 'campaignActivities':
    case 'campaign_activities':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'CAMPAIGN ACTIVITIES'),
            accessor: 'campaign_activities',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'campaign_activities')
        };
    case 'totalActivities':
    case 'total_activities':
        return {
            Header: 'TOTAL ACTIVITIES',
            accessor: 'total_activities',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'total_activities')
        };
    case 'clicks':
        return {
            Header: 'CLICKS',
            accessor: 'clicks',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'clicks')
        };
    case 'conversionRevenue':
    case 'conversion_revenue':
        return {
            Header: 'CONVERSION REVENUE',
            accessor: 'conversion_revenue',
            type: 'price',
            Cell: row => replaceNotApplicableVals(row, 'price'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'conversion_revenue')
        };
    case 'cpc':
        return {
            Header: 'CPC',
            accessor: 'cpc',
            type: 'price',
            tooltip: 'Cost Per Click',
            Aggregated: (row) => {
                const clicks = sumBy(row.subRows, 'clicks');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, clicks)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'spend', 'clicks')
        };
    case 'ctr':
        return {
            Header: 'CTR',
            accessor: 'ctr',
            type: 'percentage',
            tooltip: 'Click Through Rate',
            Aggregated: (row) => {
                const impressions = sumBy(row.subRows, 'impressions');
                const clicks = sumBy(row.subRows, 'clicks');
                return (
                    <CellDiv value={helpers.safeDivide(clicks, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'clicks', 'impressions')
        };
    case 'ecpa':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'eCPA'),
            accessor: 'ecpa',
            type: 'price',
            Aggregated: (row) => {
                const activities = exports.nestedSumBy(row, 'ecpa_activities');
                const spend = exports.nestedSumBy(row, 'spend');
                const ecpaSpend = exports.nestedSumBy(row, 'ecpa_spend');
                return (
                    <CellDiv value={helpers.safeDivide(ecpaSpend || spend, activities)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: (rows) => {
                const activities = simpleSum(rows, 'ecpa_activities');
                const spend = simpleSum(rows, 'spend');
                const ecpaAggregate = spend ? safeDivide(spend, activities) : safeDivide(simpleSum(rows, 'ecpa_spend'), activities);

                return ecpaAggregate;
            }
        };
    case 'eCPARate':
    case 'ecpa_rate':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'eCPA RATE'),
            accessor: 'ecpa_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const eCPAActivities = sumBy(row.subRows, 'ecpa_activities');
                const clicks = sumBy(row.subRows, 'clicks');
                return (
                    <CellDiv value={helpers.safeDivide(eCPAActivities, clicks)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'ecpa_activities', 'clicks')
        };
    case 'cpa':
        return {
            Header: 'CPA',
            accessor: 'cpa',
            type: 'price',
            Aggregated: (row) => {
                const totalActivities = sumBy(row.subRows, 'total_activities');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, totalActivities)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'total_activities', 'spend')
        };
    case 'activityRate':
        return {
            Header: 'ACTIVITY RATE',
            accessor: 'activity_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const totalActivities = sumBy(row.subRows, 'total_activities');
                const clicks = sumBy(row.subRows, 'clicks');
                return (
                    <CellDiv value={helpers.safeDivide(totalActivities, clicks)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'total_activities', 'clicks')
        };
    case 'spend':
        return {
            Header: 'BUDGET',
            accessor: 'spend',
            type: 'price',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'spend')
        };
    case 'ecpa_spend':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'eCPA BUDGET'),
            accessor: 'ecpa_spend',
            type: 'price',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'ecpa_spend')
        };
    case 'budget':
        return {
            Header: 'BUDGET',
            accessor: 'spend',
            type: 'price',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'spend')
        };
    case 'cpcv':
        return {
            Header: 'CPCV',
            accessor: 'cpcv',
            type: 'price',
            Aggregated: (row) => {
                const spend = exports.nestedSumBy(row, 'spend');
                const completed = sumBy(row.subRows, 'completed_plays');
                return (
                    <CellDiv value={helpers.safeDivide(spend, completed)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'completed_plays')
        };
    case 'startedPlays':
    case 'started_plays':
        return {
            Header: `${metricPrefix || ''}STARTS`,
            accessor: 'started_plays',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'started_plays')
        };
    case 'completed_plays':
        return {
            Header: `${metricPrefix || ''}COMPLETIONS`,
            accessor: 'completed_plays',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'completed_plays')
        };
    case 'completion_rate':
        return {
            Header: `${metricPrefix || ''}COMPLETION RATE`,
            accessor: 'completion_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const started = sumBy(row.subRows, 'started_plays');
                const completed = sumBy(row.subRows, 'completed_plays');
                return (
                    <CellDiv value={helpers.safeDivide(completed, started)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'completed_plays', 'started_plays')
        };
    case 'first_quartile_completions':
        return {
            Header: 'VIDEO 25% COMPLETIONS',
            accessor: 'first_quartile_completions',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'first_quartile_completions')
        };
    case 'midpoint':
        return {
            Header: 'VIDEO 50% COMPLETIONS',
            accessor: 'midpoint',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'midpoint')
        };
    case 'third_quartile_completions':
        return {
            Header: 'VIDEO 75% COMPLETIONS',
            accessor: 'third_quartile_completions',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'third_quartile_completions')
        };
    case 'gmail_saves':
        return {
            Header: 'SAVES',
            accessor: 'gmail_saves',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'gmail_saves')
        };
    case 'gmail_forwards':
        return {
            Header: 'FORWARDS',
            accessor: 'gmail_forwards',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'gmail_forwards')
        };
    case 'gmail_opens':
        return {
            Header: 'OPENS',
            accessor: 'gmail_opens',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'gmail_opens')
        };
    case 'gmail_opens_rate':
        return {
            Header: 'OPEN RATE',
            accessor: 'gmail_opens_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const opens = sumBy(row.subRows, 'gmail_opens');
                const impressions = sumBy(row.subRows, 'impressions');
                return (
                    <CellDiv value={helpers.safeDivide(opens, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'gmail_opens', 'impressions')
        };
    case 'gmail_secondary_clicks':
        return {
            Header: 'CLICKS TO SITE',
            accessor: 'gmail_secondary_clicks',
            type: 'number',
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'gmail_secondary_clicks')
        };
    case 'gmail_secondary_clicks_rate':
        return {
            Header: 'CLICKS TO SITE RATE',
            accessor: 'gmail_secondary_clicks_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const secondaryClicks = sumBy(row.subRows, 'gmail_secondary_clicks');
                const clicks = sumBy(row.subRows, 'clicks');
                return (
                    <CellDiv value={helpers.safeDivide(secondaryClicks, clicks)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'gmail_secondary_clicks', 'clicks')
        };
    case 'gmail_secondary_clicks_cpc':
        return {
            Header: 'COST PER CLICK TO SITE',
            accessor: 'gmail_secondary_clicks_cpc',
            type: 'percentage',
            Aggregated: (row) => {
                const secondaryClicks = sumBy(row.subRows, 'gmail_secondary_clicks');
                const spend = sumBy(row.subRows, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, secondaryClicks)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'gmail_secondary_clicks')
        };
    case 'link_clicks':
        return {
            Header: 'LINK CLICKS',
            accessor: 'link_clicks',
            type: 'number',
            aggregate: values => sum(values),
            Cell: row => replaceNotApplicableVals(row, 'number'),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'link_clicks')
        };
    case 'cost_per_link_click':
        return {
            Header: 'CPLC',
            accessor: 'cost_per_link_click',
            type: 'price',
            tooltip: 'Cost Per Link Click',
            Cell: (row) => {
                const hasMetric = hasCalcMetric(row, 'link_clicks');
                const [value, type] = hasMetric ? [helpers.safeDivide(row.row.original.spend, row.row.original.link_clicks), 'price'] : ['--', 'NOT_APPLICABLE'];
                return createCellDiv(value, type);
            },
            Aggregated: (row) => {
                const linkClicks = sumBy(row.subRows, 'link_clicks');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, linkClicks)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'spend', 'link_clicks')
        };
    case 'link_click_rate':
        return {
            Header: 'LCR',
            accessor: 'link_click_rate',
            type: 'percentage',
            tooltip: 'Link Click Rate',
            Cell: (row) => {
                const hasMetric = hasCalcMetric(row, 'link_clicks');
                const [value, type] = hasMetric ? [helpers.safeDivide(row.row.original.link_clicks, row.row.original.impressions), 'percentage'] : ['--', 'NOT_APPLICABLE'];
                return createCellDiv(value, type);
            },
            Aggregated: (row) => {
                const linkClicks = sumBy(row.subRows, 'link_clicks');
                const impressions = sumBy(row.subRows, 'impressions');
                return (
                    <CellDiv value={helpers.safeDivide(linkClicks, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'link_clicks', 'impressions')
        };
    case 'inline_link_clicks':
        return {
            Header: 'LINK CLICKS',
            accessor: 'inline_link_clicks',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            smartAggregate: rows => simpleSum(rows, 'inline_link_clicks')
        };
    case 'cost_per_inline_link_click':
        return {
            Header: 'CPLC',
            accessor: 'cost_per_inline_link_click',
            type: 'price',
            tooltip: 'Cost Per Link Click',
            Cell: (row) => {
                const hasMetric = hasCalcMetric(row, 'inline_link_clicks');
                const [value, type] = hasMetric ? [helpers.safeDivide(row.row.original.spend, row.row.original.inline_link_clicks), 'price'] : ['--', 'NOT_APPLICABLE'];
                return createCellDiv(value, type);
            },
            Aggregated: (row) => {
                const linkClicks = sumBy(row.subRows, 'inline_link_clicks');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, linkClicks)} type="price" />
                );
            },
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'spend', 'inline_link_clicks')
        };
    case 'conversions':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'CONVERSIONS'),
            accessor: 'conversions',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'conversions')
        };
    case 'conversionRate':
    case 'conversion_rate':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'CONVERSION RATE'),
            accessor: 'conversion_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const conversions = sumBy(row.subRows, 'conversions');
                const impressions = sumBy(row.subRows, 'impressions');
                return (
                    <CellDiv value={helpers.safeDivide(conversions, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'conversions', 'impressions')
        };
    case 'cpconv':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'COST / CONV'),
            accessor: 'cpconv',
            type: 'price',
            tooltip: 'Cost per Conversion',
            Aggregated: (row) => {
                const conversions = sumBy(row.subRows, 'conversions');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, conversions)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'conversions')
        };
    case 'cost_per_conversion':
        return {
            Header: generateLabelCodeHeaderMap(labelCode, 'COST / CONV'),
            accessor: 'cost_per_conversion',
            type: 'price',
            tooltip: 'Cost per Conversion',
            Aggregated: (row) => {
                const conversions = sumBy(row.subRows, 'conversions');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, conversions)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'conversions')
        };
    case 'engagements':
        return {
            Header: 'ENGAGEMENTS',
            accessor: 'engagements',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'engagements')
        };
    case 'engagementRate':
    case 'engagement_rate':
        return {
            Header: 'ENGAGEMENT RATE',
            accessor: 'engagement_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const engagements = sumBy(row.subRows, 'engagements');
                const impressions = sumBy(row.subRows, 'impressions');
                return (
                    <CellDiv value={helpers.safeDivide(engagements, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'engagements', 'impressions')
        };
    case 'leads':
        return {
            Header: 'LEADS',
            accessor: 'leads',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'leads')
        };
    case 'leadgen_grouped':
        return {
            Header: 'LEADS',
            accessor: 'leadgen_grouped',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'leadgen_grouped')
        };
    case 'cost_per_lead':
        return {
            Header: 'COST / LEAD',
            accessor: 'cost_per_lead',
            type: 'price',
            Aggregated: (row) => {
                const leads = sumBy(row.subRows, 'leads');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, leads)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'leads')
        };
    case 'cost_per_leadgen_grouped':
        return {
            Header: 'COST / LEAD',
            accessor: 'cost_per_leadgen_grouped',
            type: 'price',
            Aggregated: (row) => {
                const leads = sumBy(row.subRows, 'leadgen_grouped');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, leads)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'leadgen_grouped')
        };
    case 'actions':
        return {
            Header: 'ACTION COUNT',
            accessor: 'actions',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => simpleSum(rows, 'actions')
        };
    case 'cost_per_action':
        return {
            Header: 'Cost / Action',
            accessor: 'cost_per_action',
            type: 'price',
            Aggregated: (row) => {
                const actions = sumBy(row.subRows, 'actions');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, actions)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            nowrap: true,
            smartAggregate: rows => ratio(rows, 'spend', 'actions')
        };
    case 'videoViews':
    case 'video_views':
        return {
            Header: 'VIDEO VIEWS',
            accessor: 'video_views',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'video_views')
        };
    case 'videoViewRate':
    case 'video_view_rate':
        return {
            Header: 'VIDEO VIEW RATE',
            accessor: 'video_view_rate',
            type: 'percentage',
            Aggregated: (row) => {
                const videoViews = sumBy(row.subRows, 'video_views');
                const impressions = sumBy(row.subRows, 'impressions');
                return (
                    <CellDiv value={helpers.safeDivide(videoViews, impressions)} type="percentage" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'video_views', 'impressions')
        };
    case 'cpvv':
        return {
            Header: 'CPVV',
            accessor: 'cpvv',
            type: 'price',
            tooltip: 'Cost per Video View',
            Aggregated: (row) => {
                const videoViews = sumBy(row.subRows, 'video_views');
                const spend = exports.nestedSumBy(row, 'spend');
                return (
                    <CellDiv value={helpers.safeDivide(spend, videoViews)} type="price" />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => ratio(rows, 'spend', 'video_views')
        };
    case 'purchase_conversion_value':
        return {
            Header: 'CONVERSION REVENUE',
            accessor: 'purchase_conversion_value',
            type: 'price',
            Cell: row => replaceNotApplicableVals(row, 'price'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'purchase_conversion_value')
        };
    case 'purchase_roas':
        return {
            Header: 'ROAS',
            accessor: 'purchase_roas',
            type: 'floatX2',
            tooltip: 'ROAS',
            Cell: row => replaceNotApplicableVals(row, 'floatX2'),
            Aggregated: (row) => {
                const rowROAS = sumBy(row.subRows, 'purchase_roas');

                if (rowROAS > 0) {
                    const PCV = exports.nestedSumBy(row, 'purchase_conversion_value');
                    const spend = exports.nestedSumBy(row, 'spend');
                    return (
                        <CellDiv value={helpers.safeDivide(PCV, spend)} type="floatX2" />
                    );
                }
                return (
                    <CellDiv value={'N/A'} type={'string'} style={{ textAlign: 'right' }} />
                );
            },
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'purchase_roas') > 0 &&
                    ratio(rows, 'purchase_conversion_value', 'spend')
        };
    case 'reach':
        return {
            Header: 'REACH',
            accessor: 'reach',
            type: 'number',
            Cell: row => replaceNotApplicableVals(row, 'number'),
            aggregate: values => sum(values),
            filter: 'numericColumnFilter',
            smartAggregate: rows => simpleSum(rows, 'reach')
        };
    case 'grouped_type':
        return {
            Header: 'Grouped',
            type: 'text',
            Cell: row => <CellDiv value={row.value ? 'Grouped' : 'Not Grouped'} type={'boolean'} />,
            accessor: 'grouped_type',
            dataToggle: {
                filter: 'isNot',
                value: true
            },
            filter: 'booleanFilter',
            hidden: true
        };
    case 'is_ecpa':
        return {
            Header: labelCode,
            type: 'text',
            Cell: row => <CellDiv value={row.value ? `is ${labelCode}` : `not ${labelCode}`} type={'boolean'} />,
            accessor: 'ecpa',
            dataToggle: {
                filter: 'is',
                value: true
            },
            filter: 'booleanFilter',
            hidden: true
        };
    case 'publisher':
        return {
            accessor: 'publisher',
            Header: 'PUBLISHER',
            type: 'text',
            filter: 'textColumnFilter',
            dataToggle: {
                filter: 'doesNotEq',
                value: 'Other'
            },
        };
    case 'app_name':
        return {
            accessor: 'app_name',
            Header: 'App Name',
            type: 'text',
            filter: 'textColumnFilter',
            dataToggle: {
                filter: 'doesNotEq',
                value: 'Other'
            },
        };
    case 'publisherLogo':
        return {
            accessor: 'publisherLogo',
            Header: 'LOGO',
            type: 'image',
            isIndexCol: true,
            Cell: row => <ImageCellDiv image={row.value.image} />,
            disableFilters: true,
            disableGlobalFilter: true,
            nowrap: true,
            widthOverride: 1,
            sortable: false,
        };
    default:
        return null;
    }
};
exports.applyDefaultColumns = (columns, defaultCols) => columns.map((col) => {
    if (!!col && !defaultCols.includes(col.accessor)) {
        col.hidden = true;
    }
    return col;
});


/* eslint-disable camelcase */
const computedAggregations = {
    ctr: ({ clicks, impressions }) => helpers.safeDivide(clicks, impressions),
    has_delivered: () => 'N/A',
    grouped_type: () => 'N/A',
    cpc: ({ spend, clicks }) => helpers.safeDivide(spend, clicks),
    ecpa: ({ ecpa_spend, spend, ecpa_activities }) => helpers.safeDivide(ecpa_spend || spend, ecpa_activities),
    cpa: ({ spend, total_activities }) => helpers.safeDivide(spend, total_activities),
    cpcv: ({ spend, completed_plays }) => helpers.safeDivide(spend, completed_plays),
    cpconv: ({ spend, conversions }) => helpers.safeDivide(spend, conversions),
    cost_per_conversion: ({ spend, conversions }) => helpers.safeDivide(spend, conversions),
    cpvv: ({ spend, video_views }) => helpers.safeDivide(spend, video_views),
    completion_rate: ({ completed_plays, started_plays }) => helpers.safeDivide(completed_plays, started_plays),
    conversion_rate: ({ conversions, impressions }) => helpers.safeDivide(conversions, impressions),
    cost_per_action: ({ totalCampaignSpend, actions }) => helpers.safeDivide(totalCampaignSpend, actions),
    engagement_rate: ({ engagements, impressions }) => helpers.safeDivide(engagements, impressions),
    cost_per_lead: ({ spend, leads }) => helpers.safeDivide(spend, leads),
    cost_per_leadgen_grouped: ({ spend, leadgen_grouped }) => helpers.safeDivide(spend, leadgen_grouped),
    cost_per_link_click: ({ spend, link_clicks }) => helpers.safeDivide(spend, link_clicks),
    cost_per_inline_link_click: ({ spend, inline_link_clicks }) => helpers.safeDivide(spend, inline_link_clicks),
    video_view_rate: ({ video_views, impressions }) => helpers.safeDivide(video_views, impressions),
    activity_rate: ({ total_activities, clicks }) => helpers.safeDivide(total_activities, clicks),
    ecpa_rate: ({ ecpa_activities, clicks }) => helpers.safeDivide(ecpa_activities, clicks),
    gmail_secondary_clicks_rate: ({ gmail_secondary_clicks, clicks }) =>
        helpers.safeDivide(gmail_secondary_clicks, clicks),
    gmail_opens_rate: ({ gmail_opens, impressions }) => helpers.safeDivide(gmail_opens, impressions),
    purchase_roas: ({ sumPCVWithoutZeroes, sumSpendWithoutZeroes }) =>
        helpers.safeDivide(sumPCVWithoutZeroes, sumSpendWithoutZeroes),
};
/* eslint-enable camelcase */

const computedAggregationKeys = Object.keys(computedAggregations);

exports.generateFooterData = ({ data = [], filteredKeys = [], extraData = {} }) => {
    const baseKeys = Object.keys(get(data, '[0]', {}));
    const footerData = {};

    // sum up data for simple measures
    const keysToSkip = [...filteredKeys, ...computedAggregationKeys];
    for (const key of baseKeys) {
        if (!keysToSkip.includes(key)) {
            footerData[key] = sumBy(data, key);
        }
    }

    // limit computedAggregations to only those fields that are within the source data
    const footerDataWithExtra = { ...footerData, ...extraData };
    for (const key of intersection(baseKeys, computedAggregationKeys)) {
        set(footerData, key, computedAggregations[key](footerDataWithExtra));
    }

    return footerData;
};

exports.generateHeaders = ({ keys,
    footerData = {},
    initial = {},
    colOptions = {},
}) => keys.reduce((prev, col) => {
    const template = columnsTemplateLookup(col, colOptions);
    prev[col] = {
        Cell: row => <CellDiv value={row.value} type={template.type} />,
        Footer: (
            <CellDiv value={footerData[template.accessor]} type={template.type} strong />
        ),
        ...template
    };

    return prev;
}, initial);



// WEBPACK FOOTER //
// ./src/helpers/tables.js